import React, { useState } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaShieldAlt,
  FaCheckCircle,
  FaArrowLeft,
} from "react-icons/fa";

function ChangePassword() {
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [saving, setSaving] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;
    return regex.test(password);
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password) && /[@$!%*?&]/.test(password)) strength += 25;
    return strength;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "newPassword") {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 25) return "#ef4444";
    if (passwordStrength <= 50) return "#f59e0b";
    if (passwordStrength <= 75) return "#3b82f6";
    return "#22c55e";
  };

  const getStrengthLabel = () => {
    if (passwordStrength <= 25) return "Weak";
    if (passwordStrength <= 50) return "Fair";
    if (passwordStrength <= 75) return "Good";
    return "Strong";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.oldPassword) {
      toast.error("Please enter your current password");
      return;
    }

    if (!formData.newPassword) {
      toast.error("Please enter a new password");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    if (!validatePassword(formData.newPassword)) {
      toast.error(
        "Password must be at least 8 characters with uppercase, lowercase, number, and special character"
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        currentPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      };
      await allAPiServicesCall.changePassword(payload, getAuthConfig(), navigate);
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
      padding: "40px 20px",
    },
    wrapper: {
      maxWidth: "500px",
      margin: "0 auto",
    },
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 24px",
      background: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#64748b",
      cursor: "pointer",
      marginBottom: "24px",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
    },
    headerCard: {
      background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
      borderRadius: "24px",
      padding: "40px",
      marginBottom: "24px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    },
    headerPattern: {
      position: "absolute",
      top: 0,
      right: 0,
      width: "200px",
      height: "200px",
      background:
        "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
      borderRadius: "50%",
      transform: "translate(30%, -30%)",
    },
    headerIcon: {
      width: "80px",
      height: "80px",
      background: "linear-gradient(135deg, #0031E2 0%, #1444F0 100%)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 20px",
      fontSize: "32px",
      color: "white",
      boxShadow: "0 10px 30px rgba(0, 49, 226, 0.4)",
    },
    headerTitle: {
      fontSize: "28px",
      fontWeight: "700",
      color: "white",
      margin: "0 0 8px 0",
    },
    headerSubtitle: {
      fontSize: "14px",
      color: "rgba(255, 255, 255, 0.7)",
      margin: 0,
    },
    formCard: {
      background: "white",
      borderRadius: "20px",
      padding: "32px",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
    },
    formGroup: {
      marginBottom: "24px",
    },
    label: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "8px",
    },
    labelIcon: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
    },
    inputWrapper: {
      position: "relative",
    },
    input: {
      width: "100%",
      padding: "16px 50px 16px 20px",
      fontSize: "16px",
      border: "2px solid #e2e8f0",
      borderRadius: "12px",
      outline: "none",
      transition: "all 0.3s ease",
      backgroundColor: "#f8fafc",
    },
    eyeButton: {
      position: "absolute",
      right: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      color: "#94a3b8",
      cursor: "pointer",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "color 0.3s ease",
    },
    strengthContainer: {
      marginTop: "12px",
    },
    strengthBar: {
      height: "6px",
      background: "#e2e8f0",
      borderRadius: "3px",
      overflow: "hidden",
      marginBottom: "8px",
    },
    strengthFill: {
      height: "100%",
      borderRadius: "3px",
      transition: "all 0.3s ease",
    },
    strengthLabel: {
      fontSize: "12px",
      fontWeight: "600",
    },
    requirements: {
      marginTop: "16px",
      padding: "16px",
      background: "#f8fafc",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
    },
    requirementsTitle: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "12px",
    },
    requirementItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "12px",
      color: "#64748b",
      marginBottom: "6px",
    },
    requirementIcon: {
      fontSize: "12px",
    },
    button: {
      width: "100%",
      padding: "16px 32px",
      borderRadius: "14px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      transition: "all 0.3s ease",
      background: "linear-gradient(135deg, #0031E2 0%, #1444F0 100%)",
      color: "white",
      boxShadow: "0 10px 30px rgba(0, 49, 226, 0.4)",
      marginTop: "8px",
    },
    disabledButton: {
      opacity: 0.7,
      cursor: "not-allowed",
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255,255,255,0.3)",
      borderTopColor: "white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  const spinnerKeyframes = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  const checkRequirement = (check) => {
    const password = formData.newPassword;
    switch (check) {
      case "length":
        return password.length >= 8;
      case "lowercase":
        return /[a-z]/.test(password);
      case "uppercase":
        return /[A-Z]/.test(password);
      case "number":
        return /[0-9]/.test(password);
      case "special":
        return /[@$!%*?&]/.test(password);
      default:
        return false;
    }
  };

  return (
    <CommonLayout>
      <style>{spinnerKeyframes}</style>
      <div style={styles.container}>
        <div style={styles.wrapper}>
          {/* Back Button */}
          <button
            style={styles.backButton}
            onClick={() => navigate("/profile")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateX(-4px)";
              e.currentTarget.style.color = "#1a1a2e";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateX(0)";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            <FaArrowLeft /> Back to Profile
          </button>

          {/* Header */}
          <div style={styles.headerCard}>
            <div style={styles.headerPattern}></div>
            <div style={styles.headerIcon}>
              <FaShieldAlt />
            </div>
            <h1 style={styles.headerTitle}>Change Password</h1>
            <p style={styles.headerSubtitle}>
              Keep your account secure with a strong password
            </p>
          </div>

          {/* Form Card */}
          <div style={styles.formCard}>
            <form onSubmit={handleSubmit}>
              {/* Current Password */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div
                    style={{
                      ...styles.labelIcon,
                      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "white",
                    }}
                  >
                    <FaLock />
                  </div>
                  Current Password
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    name="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    placeholder="Enter current password"
                    style={styles.input}
                    autoComplete="current-password"
                    onFocus={(e) => {
                      e.target.style.borderColor = "#0031E2";
                      e.target.style.backgroundColor = "white";
                      e.target.style.boxShadow = "0 0 0 4px rgba(0, 49, 226, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.backgroundColor = "#f8fafc";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => togglePasswordVisibility("old")}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#0031E2")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                  >
                    {showPasswords.old ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div
                    style={{
                      ...styles.labelIcon,
                      background: "linear-gradient(135deg, #0031E2 0%, #1444F0 100%)",
                      color: "white",
                    }}
                  >
                    <FaKey />
                  </div>
                  New Password
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    style={styles.input}
                    autoComplete="new-password"
                    onFocus={(e) => {
                      e.target.style.borderColor = "#0031E2";
                      e.target.style.backgroundColor = "white";
                      e.target.style.boxShadow = "0 0 0 4px rgba(0, 49, 226, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.backgroundColor = "#f8fafc";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => togglePasswordVisibility("new")}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#0031E2")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                  >
                    {showPasswords.new ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div style={styles.strengthContainer}>
                    <div style={styles.strengthBar}>
                      <div
                        style={{
                          ...styles.strengthFill,
                          width: `${passwordStrength}%`,
                          background: getStrengthColor(),
                        }}
                      ></div>
                    </div>
                    <span
                      style={{
                        ...styles.strengthLabel,
                        color: getStrengthColor(),
                      }}
                    >
                      {getStrengthLabel()}
                    </span>
                  </div>
                )}

                {/* Password Requirements */}
                <div style={styles.requirements}>
                  <p style={styles.requirementsTitle}>Password Requirements:</p>
                  {[
                    { key: "length", text: "At least 8 characters" },
                    { key: "lowercase", text: "One lowercase letter" },
                    { key: "uppercase", text: "One uppercase letter" },
                    { key: "number", text: "One number" },
                    { key: "special", text: "One special character (@$!%*?&)" },
                  ].map((req) => (
                    <div key={req.key} style={styles.requirementItem}>
                      <FaCheckCircle
                        style={{
                          ...styles.requirementIcon,
                          color: checkRequirement(req.key) ? "#22c55e" : "#cbd5e1",
                        }}
                      />
                      <span
                        style={{
                          color: checkRequirement(req.key) ? "#22c55e" : "#64748b",
                        }}
                      >
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Confirm Password */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <div
                    style={{
                      ...styles.labelIcon,
                      background: "linear-gradient(135deg, #1DB954 0%, #17a34a 100%)",
                      color: "white",
                    }}
                  >
                    <FaCheckCircle />
                  </div>
                  Confirm New Password
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    style={{
                      ...styles.input,
                      borderColor:
                        formData.confirmPassword &&
                        formData.confirmPassword === formData.newPassword
                          ? "#22c55e"
                          : formData.confirmPassword
                          ? "#ef4444"
                          : "#e2e8f0",
                    }}
                    autoComplete="new-password"
                    onFocus={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.boxShadow = "0 0 0 4px rgba(0, 49, 226, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.backgroundColor = "#f8fafc";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => togglePasswordVisibility("confirm")}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#0031E2")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                  >
                    {showPasswords.confirm ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                  </button>
                </div>
                {formData.confirmPassword && (
                  <small
                    style={{
                      color:
                        formData.confirmPassword === formData.newPassword
                          ? "#22c55e"
                          : "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {formData.confirmPassword === formData.newPassword
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </small>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(saving ? styles.disabledButton : {}),
                }}
                disabled={saving}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 15px 40px rgba(0, 49, 226, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(0, 49, 226, 0.4)";
                }}
              >
                {saving ? (
                  <>
                    <div style={styles.spinner}></div>
                    Updating Password...
                  </>
                ) : (
                  <>
                    <FaShieldAlt /> Update Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}

export default ChangePassword;
